import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import posthog, { CaptureResult, Properties } from 'posthog-js';
import { filter } from 'rxjs/operators';
import {
    AnalyticsEventArgs,
    AnalyticsEvents,
    AnalyticsTracker,
    hidesIdentityFromAnalytics,
    isInternalEmail,
    OrderEventProperties,
    Profile
} from '@trade-platform/ui-shared';
import { getFromStorage, LogService, setToStorage } from '@trade-platform/ui-utils';
import { environment } from '../environments/environment';
import {
    maskReplayAttribute,
    maskReplayInput,
    maskReplayText,
    REPLAY_BLOCK_SELECTOR,
    scrubCapturedUrls
} from './posthog-privacy';

/** The deployment name (`uat`, `production`, `local`, ...). Each environment has its own PostHog project. */
export const posthogEnvironment = environment.environment || environment.id;

/**
 * True when PostHog already holds a different identified user than the one logging in. PostHog
 * persists `$user_state` as `identified` after `identify()` and `anonymous` after `reset()`.
 */
export function isDifferentIdentifiedUser(
    userState: unknown,
    currentDistinctId: unknown,
    nextDistinctId: string
): boolean {
    return userState === 'identified' && currentDistinctId !== nextDistinctId;
}

/**
 * The tenant a host serves. Each tenant has its own subdomain, named for its tenantIdentifier
 * (`firm.aixplatform.com`, `firm.uat.aixplatform.com`), so events are tagged before login too.
 */
export function tenantFromHost(hostname: string): string | undefined {
    const labels = hostname.split('.');
    // A tenant is a subdomain, so the bare domain, localhost and IP addresses name none.
    if (labels.length < 3 || /^\d+$/.test(labels[labels.length - 1])) {
        return undefined;
    }

    return labels[0];
}

/**
 * `before_send` hook: drops `$autocapture` events. With element text and attributes masked, each
 * one records only tag names ("clicked an input inside a form"), so they can't answer a question or
 * back a PostHog Action, and they swamped the activity feed. Autocapture itself stays on, because
 * `$rageclick` is only detected inside it; rage clicks are sent under their own event name.
 */
export function dropAutocapture(event: CaptureResult | null): CaptureResult | null {
    return event?.event === '$autocapture' ? null : event;
}

/**
 * localStorage key holding, per group type, the `<session id>:<group key>` whose properties were last
 * sent. It outlives page loads, which is where the repeated `$groupidentify` events came from.
 */
const GROUPS_SENT_STORAGE_KEY = 'posthog_groups_sent';

function readGroupsSent(): Record<string, string> {
    try {
        const sent = getFromStorage<Record<string, string>>(GROUPS_SENT_STORAGE_KEY);
        return sent && typeof sent === 'object' ? sent : {};
    } catch {
        // Without storage, group properties are sent on every page load, as before.
        return {};
    }
}

function writeGroupsSent(sent: Record<string, string>): void {
    try {
        setToStorage(GROUPS_SENT_STORAGE_KEY, sent);
    } catch {
        // See readGroupsSent.
    }
}

export function isDemoTenant(tenant: string): boolean {
    return tenant.endsWith('-demo');
}

export function tenantProperties(tenant: string | undefined): Properties {
    return tenant ? { tenant, is_demo_tenant: isDemoTenant(tenant) } : {};
}

function organizationName(profile: Profile): string | undefined {
    switch (profile.organization?.type) {
        case 'firm':
            return profile.firm?.name;
        case 'fundSponsor':
            return profile.fundSponsor?.name;
        case 'holdingOption':
            return profile.holdingOption?.name;
        case 'transferAgent':
            // /me carries no transfer agent record (and the profile decoder keeps none), so there is
            // no name to send.
            return undefined;
        default:
            return undefined;
    }
}

function isOrderEvent(properties: object | undefined): properties is OrderEventProperties {
    const orderId = (properties as Partial<OrderEventProperties> | undefined)?.order_id;
    return typeof orderId === 'string' && orderId !== '';
}

/**
 * An order event's properties with the order in its own `$groups`. posthog-js merges a non-empty
 * event `$groups` over the registered ones for this event only, so `tenant` stays, and no later
 * event carries the order (as it would after `group('order', ...)`). Like the backend's
 * `order_status_changed`, the organization group is the order's firm when it is known, and the
 * user's own organization (a `<type>:<entityId>` group key) is `actor_org_id`.
 */
export function withOrderGroups(
    properties: OrderEventProperties,
    actorOrgId: string | undefined
): Properties {
    return {
        ...properties,
        ...(actorOrgId ? { actor_org_id: actorOrgId } : {}),
        $groups: {
            order: properties.order_id,
            ...(properties.firm_id === undefined
                ? {}
                : { organization: `firm:${properties.firm_id}` })
        }
    };
}

/** The Auth0 callback route is the only one recognised by a matcher, so it has no path. */
const AUTH_CALLBACK_ROUTE = 'auth-callback';

/**
 * The route template of a navigation, such as `orders/buy/:id`: each level's configured path joined
 * from root to leaf. Hash routing leaves `$pathname` as `/` on every page, and the URL itself holds
 * IDs, so this is the page name events are grouped by.
 */
export function routeTemplate(root: ActivatedRouteSnapshot): string {
    const parts: string[] = [];
    for (let node: ActivatedRouteSnapshot | null = root; node; node = node.firstChild) {
        const config = node.routeConfig;
        // `''` wrappers add nothing; the wildcard stays `**`, which counts not-found hits.
        if (config?.path) {
            parts.push(config.path);
        } else if (config?.matcher) {
            parts.push(AUTH_CALLBACK_ROUTE);
        }
    }
    return parts.join('/') || '/';
}

/**
 * The feature names a user is entitled to, sorted so the value only changes when the set does, plus
 * booleans for the features analysed most often (PostHog filters an array only by string match).
 */
function featureProperties(profile: Profile): Properties {
    const features = (profile.features ?? []).map(feature => feature.name).sort();

    return {
        features,
        has_explore_product: features.includes('exploreProduct'),
        has_account_maintenance: features.includes('accountMaintenance'),
        has_selling_agreements: features.includes('sellingAgreements')
    };
}

/**
 * A pure investor login (only the `activeInvestor` role, not AIX staff) is identified by ID only.
 * Everyone else is a business user of the platform, so name and email are included.
 */
export function posthogPersonProperties(profile: Profile): Properties {
    const contact = hidesIdentityFromAnalytics(profile)
        ? {}
        : { email: profile.email, name: profile.fullName };

    return {
        ...contact,
        roles: profile.roles.map(role => role.name),
        tenant: profile.organization?.tenantIdentifier,
        org_type: profile.organization?.type,
        org_entity_id: profile.organization?.entityId,
        org_name: organizationName(profile),
        ...featureProperties(profile),
        is_internal: isInternalEmail(profile.email),
        environment: posthogEnvironment
    };
}

@Injectable({ providedIn: 'root' })
export class PostHogService implements AnalyticsTracker {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly document = inject(DOCUMENT);
    private readonly injector = inject(Injector);
    private readonly LOG = inject(LogService).getLogger('posthog.service');
    private initialized = false;
    /** The current route template, kept so `reset()` can register it again. */
    private route?: string;

    get posthog(): typeof posthog {
        if (isPlatformBrowser(this.platformId) && this.initialized) {
            return posthog;
        }

        return new Proxy({} as typeof posthog, {
            get: () => () => undefined
        });
    }

    get logger(): typeof posthog.logger {
        if (isPlatformBrowser(this.platformId) && this.initialized) {
            return posthog.logger;
        }

        return new Proxy({} as typeof posthog.logger, {
            get: () => () => undefined
        });
    }

    init(): void {
        if (!isPlatformBrowser(this.platformId) || this.initialized) {
            return;
        }

        const { projectToken, host } = environment.posthog;
        if (!this.isConfigured(projectToken) || !this.isConfigured(host)) {
            this.LOG.warn(
                'PostHog is disabled: NG_APP_POSTHOG_PROJECT_TOKEN and NG_APP_POSTHOG_HOST are not configured.'
            );
            return;
        }

        posthog.init(projectToken, {
            api_host: host,
            // Keep identity per host. The default stores it on .aixplatform.com, which every tenant
            // subdomain and both UAT and production share, so tabs on different accounts would
            // overwrite one another.
            cross_subdomain_cookie: false,
            // Not '2026-06-25' or later: those strip URL hashes, and this app routes by hash.
            defaults: '2026-05-30',
            // Automatic pageviews fire only when `pathname` changes, which hash routing never does,
            // so pageviews are captured on each navigation instead (see `capturePageviews`).
            capture_pageview: false,
            // The default ('if_capture_pageview') would stop page-leave events along with pageviews.
            capture_pageleave: true,
            before_send: [dropAutocapture, scrubCapturedUrls],
            // Autocapture: element text and attributes can hold PII, so neither is sent.
            mask_all_text: true,
            mask_all_element_attributes: true,
            session_recording: {
                maskAllInputs: true,
                maskInputFn: maskReplayInput,
                maskTextSelector: '*',
                maskTextFn: maskReplayText,
                maskAttributeFn: maskReplayAttribute,
                blockSelector: REPLAY_BLOCK_SELECTOR,
                // Off in code so the project setting cannot turn it on: pdf.js draws documents to canvas.
                captureCanvas: { recordCanvas: false },
                recordHeaders: false,
                recordBody: false
            },
            enable_recording_console_log: false,
            // Network timing records request URLs, whose query strings can carry search terms.
            capture_performance: { network_timing: false, web_vitals: true },
            logs: {
                serviceName: 'advisor-ui',
                environment: posthogEnvironment,
                serviceVersion: environment.appVersion
            }
        });
        this.initialized = true;
        // `register()` persists super properties, so the last load's route would otherwise stay on
        // this load's events until its first navigation completes.
        this.posthog.unregister('route');
        this.registerSuperProperties();
        this.capturePageviews();
    }

    identify(profile: Profile): void {
        // The opaque User.publicId from /me; the integer row id stays on the server.
        const distinctId = profile.publicId;
        if (!distinctId) {
            return;
        }

        // Not every sign-out reaches the logout page (the login state machine can log out on its
        // own, and sessions also just expire), so a different account logging in starts clean here.
        if (
            isDifferentIdentifiedUser(
                this.posthog.get_property('$user_state'),
                this.posthog.get_distinct_id(),
                distinctId
            )
        ) {
            this.reset();
        }
        this.posthog.identify(distinctId, posthogPersonProperties(profile));

        const tenant = profile.organization?.tenantIdentifier;
        if (tenant) {
            // The profile is authoritative; the host only stands in until login.
            this.posthog.register(tenantProperties(tenant));
            // Grouped only once identified: a group call on an anonymous visitor creates a person.
            // Group properties stay tenant-level, because one tenant can hold several organizations.
            this.groupOncePerSession('tenant', tenant, {
                name: tenant,
                is_demo_tenant: isDemoTenant(tenant)
            });
        }

        const organization = profile.organization;
        if (organization) {
            // `entityId` is only unique within an organization type (firm 5 and fund sponsor 5 both
            // exist), so the type is part of the key, which the server can also build from an order.
            this.groupOncePerSession(
                'organization',
                `${organization.type}:${organization.entityId}`,
                {
                    name: organizationName(profile),
                    org_type: organization.type,
                    tenant
                }
            );
        }
    }

    /**
     * Records a sign-out and starts a fresh anonymous identity, but only for a visitor PostHog has
     * identified. `/logout` is also how the app sends a visitor with no session to the Auth0 login, and
     * resetting there would discard the anonymous id they come back from Auth0 with: their pre-login
     * pageviews would never merge into them, and every login would leave an orphaned visitor behind.
     */
    logout(): void {
        if (this.posthog.get_property('$user_state') !== 'identified') {
            return;
        }
        this.track('logout_initiated');
        this.reset();
    }

    /**
     * Sends a custom event from the `AnalyticsEvents` catalog. Custom events go through here rather
     * than `posthog.capture`, so their names and properties are checked against the catalog. An
     * order event (one with an `order_id`) is tagged with its order group by `withOrderGroups`.
     */
    track<K extends keyof AnalyticsEvents>(name: K, ...properties: AnalyticsEventArgs<K>): void {
        const [eventProperties] = properties;
        if (isOrderEvent(eventProperties)) {
            this.posthog.capture(
                name,
                withOrderGroups(eventProperties, this.posthog.getGroups()?.['organization'])
            );
            return;
        }
        this.posthog.capture(name, ...properties);
    }

    /** `reset()` also clears super properties and groups, so the super properties are registered again. */
    reset(): void {
        this.posthog.reset();
        this.registerSuperProperties();
    }

    /**
     * Groups the user, sending the group's properties at most once per PostHog session. posthog-js
     * sends a `$groupidentify` on every `group()` call that has properties, and `identify()` runs on
     * every page load. The group key itself persists until `reset()`, so later events stay grouped.
     */
    private groupOncePerSession(type: string, key: string, properties: Properties): void {
        const sentFor = `${this.posthog.get_session_id()}:${key}`;
        const groupsSent = readGroupsSent();
        if (this.posthog.getGroups()?.[type] === key && groupsSent[type] === sentFor) {
            return;
        }

        this.posthog.group(type, key, properties);
        writeGroupsSent({ ...groupsSent, [type]: sentFor });
    }

    /** Super properties go on every event, including anonymous ones before login. */
    private registerSuperProperties(): void {
        this.posthog.register({
            environment: posthogEnvironment,
            ...tenantProperties(tenantFromHost(this.document.location.hostname)),
            ...(this.route ? { route: this.route } : {})
        });
    }

    /**
     * Registers the route template and captures a pageview once each navigation completes, so the
     * pageview and every event after it carry the new route. The app initializer runs before the
     * first navigation, so the initial page load is captured too.
     */
    private capturePageviews(): void {
        // Looked up here, not injected: if building the Router threw, PostHogErrorHandler would need
        // this service while the Router was still being constructed (NG0200).
        const router = this.injector.get(Router);
        router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => {
                this.route = routeTemplate(router.routerState.snapshot.root);
                this.posthog.register({ route: this.route });
                this.posthog.capture('$pageview');
            });
    }

    private isConfigured(value: string): boolean {
        return Boolean(value) && !value.startsWith('{{{');
    }
}
